import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Lock, User, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Real auth is async: the profile (user) loads via onAuthStateChanged a moment
  // after signIn resolves. Navigate only once it's actually set, so we don't bounce
  // off ProtectedRoute. Also redirects an already-logged-in user away from /login.
  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  // Load saved username on mount (password is never stored)
  useEffect(() => {
    const savedId = localStorage.getItem('fym_remember_id');
    // Clean up any previously stored password (migration)
    localStorage.removeItem('fym_remember_password');
    if (savedId) {
      setUserId(savedId);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(userId, password);

    if (result.success) {
      // Save or clear username only — password is never stored
      if (rememberMe) {
        localStorage.setItem('fym_remember_id', userId);
      } else {
        localStorage.removeItem('fym_remember_id');
      }
      // Navigation happens in the effect above once the profile (user) is loaded;
      // keep isLoading until then so the button stays in its loading state.
    } else {
      setError(result.error);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="w-full max-w-md px-4">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_bizops-manager-4/artifacts/gglb72zf_LOGO_FYM-01-03.webp" 
            alt="FLASHYOURMEME" 
            className="h-32 w-auto mx-auto"
          />
        </div>

        {/* Login Card */}
        <Card className="border-0 shadow-2xl" style={{ backgroundColor: '#1a1a1a' }}>
          <CardContent className="p-8">
            <h2 className="text-xl font-semibold text-white mb-6 text-center">เข้าสู่ระบบ</h2>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* User ID Field */}
              <div className="space-y-2">
                <Label htmlFor="userId" className="text-gray-400">ID</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                  <Input
                    id="userId"
                    type="text"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="ID"
                    className="pl-10 bg-black/50 border-gray-700 text-white placeholder:text-gray-600 focus:border-lime-500"
                    required
                    data-testid="login-userid-input"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-400">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="pl-10 bg-black/50 border-gray-700 text-white placeholder:text-gray-600 focus:border-lime-500"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              {/* Remember Me */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="rememberMe"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked)}
                  className="border-gray-600 data-[state=checked]:bg-[#BFFF00] data-[state=checked]:border-[#BFFF00]"
                  data-testid="remember-me-checkbox"
                />
                <Label htmlFor="rememberMe" className="text-gray-400 text-sm cursor-pointer">
                  จดจำการเข้าสู่ระบบ
                </Label>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 p-3 rounded-lg">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full font-semibold py-5"
                style={{ backgroundColor: '#a3e635', color: '#000' }}
                data-testid="login-submit-button"
              >
                {isLoading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-gray-600 text-sm mt-6">
          © 2026 FLASHYOURMEME. All rights reserved.
        </p>
      </div>
    </div>
  );
}
